import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import mapboxgl from 'mapbox-gl'
import { Icon } from '../../atoms/Icon'
import { IconButton } from '../../atoms/IconButton'
import { Badge } from '../../atoms/Badge'
import { Text } from '../../atoms/Text'
import { Loading } from '../../atoms/Loading'
import {
  ResizableDetailPanel,
  DetailPanelHeader,
  DetailPanelContent,
} from '../ResizableDetailPanel'
import { BuildingPermitDetailProps } from './BuildingPermitDetail.types'
import { MAPBOX_TOKEN } from '../../../config/constants'
import * as S from './BuildingPermitDetail.styles'

// Set Mapbox access token
mapboxgl.accessToken = MAPBOX_TOKEN

export const BuildingPermitDetail: React.FC<BuildingPermitDetailProps> = ({
  isOpen,
  permit,
  loading = false,
  error = null,
  onClose,
  // Helper functions passed as props
  formatAddress,
  getTotalLandArea,
  getPrimaryOfficialId,
  getActiveRegblFlags,
  getParticipantsByRoleGrouped,
  formatLandArea,
  contained,
}) => {
  const { t } = useTranslation()
  const mapRef = useRef<HTMLDivElement>(null)
  const mapInstanceRef = useRef<mapboxgl.Map | null>(null)

  // Initialize map
  useEffect(() => {
    if (!mapRef.current || !permit?.geo_center || !MAPBOX_TOKEN) return

    // Parse geo_center - Elasticsearch geo_point can be in different formats
    let lon: number | undefined
    let lat: number | undefined

    if (typeof permit.geo_center === 'string') {
      // Elasticsearch geo_point string format can be "lat,lon" or "lon,lat"
      // Need to check which format is used
      const parts = permit.geo_center.split(',')
      if (parts.length === 2) {
        const val1 = parseFloat(parts[0])
        const val2 = parseFloat(parts[1])

        // Switzerland is roughly at lat: 46-47, lon: 6-10
        // If first value is in lat range and second in lon range, it's "lat,lon"
        // Otherwise it might be "lon,lat"
        if (val1 >= 45 && val1 <= 48 && val2 >= 5 && val2 <= 11) {
          lat = val1
          lon = val2
        } else if (val2 >= 45 && val2 <= 48 && val1 >= 5 && val1 <= 11) {
          lat = val2
          lon = val1
        } else {
          // Default to lat,lon format
          lat = val1
          lon = val2
        }
      }
    } else if (Array.isArray(permit.geo_center)) {
      // Format: [lon, lat] array
      lon = permit.geo_center[0]
      lat = permit.geo_center[1]
    } else if (
      permit.geo_center &&
      typeof permit.geo_center === 'object' &&
      'lat' in permit.geo_center &&
      'lon' in permit.geo_center
    ) {
      // Format: { lat, lon } object
      lat = permit.geo_center.lat
      lon = permit.geo_center.lon
    }

    // Validate coordinates
    if (
      typeof lon !== 'number' ||
      typeof lat !== 'number' ||
      isNaN(lon) ||
      isNaN(lat)
    ) {
      console.warn('Invalid geo_center coordinates:', permit.geo_center)
      return
    }

    // Debug log to verify coordinates
    console.log('Map coordinates:', { lat, lon, raw: permit.geo_center })

    // Cleanup previous map
    if (mapInstanceRef.current) {
      mapInstanceRef.current.remove()
      mapInstanceRef.current = null
    }

    try {
      const map = new mapboxgl.Map({
        container: mapRef.current,
        style: 'mapbox://styles/mapbox/light-v11',
        center: [lon, lat],
        zoom: 15,
        interactive: true, // Allow basic interaction
        attributionControl: false,
      })

      // Wait for map to load before adding marker
      map.on('load', () => {
        // Add marker
        new mapboxgl.Marker({ color: '#2563eb' })
          .setLngLat([lon, lat])
          .addTo(map)
      })

      mapInstanceRef.current = map
    } catch (error) {
      console.error('Error initializing map:', error)
    }

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove()
        mapInstanceRef.current = null
      }
    }
  }, [permit])

  // Format date helper
  const formatDate = (dateString: string | Date | undefined) => {
    if (!dateString) return '—'
    const date = new Date(dateString)
    return date.toLocaleDateString('fr-CH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  }

  // Get status badge configuration
  const getStatusBadge = (status: string | undefined) => {
    if (!status) return null

    const upperStatus = status.toUpperCase()
    let variant: 'success' | 'warning' | 'danger' | 'primary' | 'secondary' =
      'secondary'
    let labelKey = 'detail.status.realized'

    switch (upperStatus) {
      case 'REALISE':
        variant = 'success'
        labelKey = 'detail.status.realized'
        break
      case 'ACCEPTE':
        variant = 'success'
        labelKey = 'detail.status.accepted'
        break
      case 'CHANTIER':
        variant = 'warning'
        labelKey = 'detail.status.construction'
        break
      case 'RECOURS':
        variant = 'warning'
        labelKey = 'detail.status.appeal'
        break
      case 'REFUSE':
        variant = 'danger'
        labelKey = 'detail.status.refused'
        break
      case 'CADUQUE':
        variant = 'danger'
        labelKey = 'detail.status.expired'
        break
      case 'TERMINE':
        variant = 'primary'
        labelKey = 'detail.status.completed'
        break
      case 'ARCHIVE':
        variant = 'secondary'
        labelKey = 'detail.status.archived'
        break
      case 'ABANDONNE':
        variant = 'secondary'
        labelKey = 'detail.status.abandoned'
        break
      default:
        return (
          <Badge variant={variant} size="small">
            {status}
          </Badge>
        )
    }

    return (
      <Badge variant={variant} size="small">
        {t(`buildingPermits.${labelKey}`)}
      </Badge>
    )
  }

  // Calculate metrics for header
  const landArea =
    permit && getTotalLandArea ? getTotalLandArea(permit) : undefined
  const participantsCount = permit?.participants?.length || 0
  const featuresCount = permit?.features?.length || 0

  // Get official ID for title
  const officialId =
    permit && getPrimaryOfficialId ? getPrimaryOfficialId(permit) : permit?.id

  // Get address for subtitle
  const address = permit && formatAddress ? formatAddress(permit) : '—'

  // Get status
  const status = permit?.official_final_decision || permit?.latest_status_db

  // Get active RegBL flags
  const regblFlags =
    permit && getActiveRegblFlags ? getActiveRegblFlags(permit) : []

  // Get participants grouped by role
  const participantsByRole =
    permit && getParticipantsByRoleGrouped
      ? getParticipantsByRoleGrouped(permit)
      : {}

  return (
    <ResizableDetailPanel
      isOpen={isOpen}
      onClose={onClose}
      storageKey="building_permit_detail_panel_width"
      defaultWidth="900px"
      minWidth={400}
      maxWidthPercentage={0.8}
      contained={contained}
    >
      <DetailPanelHeader onClose={onClose}>
        <S.DetailHeaderInfo>
          <Text variant="caption" color="secondary">
            {t('buildingPermits.detail.type')}
          </Text>
        </S.DetailHeaderInfo>
        <S.HeaderActions>
          <IconButton variant="default" aria-label={t('common.save')}>
            <Icon name="heart" size="medium" />
          </IconButton>
        </S.HeaderActions>
      </DetailPanelHeader>

      <DetailPanelContent>
        {loading && (
          <S.LoadingState>
            <Loading />
            <span>{t('buildingPermits.detail.loading')}</span>
          </S.LoadingState>
        )}

        {error && (
          <S.ErrorState>
            <h3>{t('buildingPermits.detail.error')}</h3>
            <p>{error}</p>
          </S.ErrorState>
        )}

        {permit && !loading && (
          <>
            {/* Permit Info Section - Title, Address, Metrics, Map */}
            <S.PermitInfoSection>
              <S.PermitInfoMainContainer>
                <S.PermitInfoContent>
                  <S.DetailTitle>
                    <Icon name="file-text" size="small" />
                    {t('buildingPermits.detail.title', { id: officialId })}
                    {status && getStatusBadge(status)}
                  </S.DetailTitle>
                  <S.DetailSubtitle>
                    <Icon name="map-pin" size="small" />
                    {address}
                    {permit?.inquiry_start_date && (
                      <>
                        {' • '}
                        {t('buildingPermits.detail.inquiry')}{' '}
                        {formatDate(permit.inquiry_start_date)}
                      </>
                    )}
                  </S.DetailSubtitle>
                </S.PermitInfoContent>

                {/* Map on right side */}
                {permit?.geo_center && MAPBOX_TOKEN && (
                  <S.PermitMapContainer ref={mapRef} />
                )}
              </S.PermitInfoMainContainer>

              {/* Metrics Row */}
              <S.HeaderMetrics>
                <S.MetricItem>
                  <S.MetricLabel>
                    {t('buildingPermits.detail.metrics.landArea')}
                  </S.MetricLabel>
                  <S.MetricValue $highlight>
                    {landArea
                      ? formatLandArea
                        ? formatLandArea(landArea)
                        : `${landArea.toLocaleString('de-CH')} m²`
                      : '—'}
                  </S.MetricValue>
                </S.MetricItem>
                <S.MetricItem>
                  <S.MetricLabel>
                    {t('buildingPermits.detail.metrics.participants')}
                  </S.MetricLabel>
                  <S.MetricValue>{participantsCount}</S.MetricValue>
                </S.MetricItem>
                <S.MetricItem>
                  <S.MetricLabel>
                    {t('buildingPermits.detail.metrics.features')}
                  </S.MetricLabel>
                  <S.MetricValue>{featuresCount}</S.MetricValue>
                </S.MetricItem>
              </S.HeaderMetrics>
            </S.PermitInfoSection>

            <S.DetailContent>
            {/* Section 1: Permit Information */}
            <S.DataGroup>
              <S.DataSectionTitle>
                <Icon name="info" size="small" />
                {t('buildingPermits.detail.sections.permitInfo')}
              </S.DataSectionTitle>
              <S.DataGrid>
                <S.DataColumn>
                  <S.DataRow>
                    <S.DataLabel>
                      {t('buildingPermits.detail.fields.officialId')}
                    </S.DataLabel>
                    <S.DataValue>{officialId}</S.DataValue>
                  </S.DataRow>
                  {permit.fao_id && (
                    <S.DataRow>
                      <S.DataLabel>
                        {t('buildingPermits.detail.fields.faoId')}
                      </S.DataLabel>
                      <S.DataValue>{permit.fao_id}</S.DataValue>
                    </S.DataRow>
                  )}
                  <S.DataRow>
                    <S.DataLabel>
                      {t('buildingPermits.detail.fields.status')}
                    </S.DataLabel>
                    <S.DataValue>
                      {status && getStatusBadge(status)}
                    </S.DataValue>
                  </S.DataRow>
                  {permit.language && (
                    <S.DataRow>
                      <S.DataLabel>
                        {t('buildingPermits.detail.fields.language')}
                      </S.DataLabel>
                      <S.DataValue>{permit.language.toUpperCase()}</S.DataValue>
                    </S.DataRow>
                  )}
                </S.DataColumn>

                <S.DataColumn>
                  <S.DataRow>
                    <S.DataLabel>
                      {t('buildingPermits.detail.fields.inquiryStart')}
                    </S.DataLabel>
                    <S.DataValue>
                      {formatDate(permit.inquiry_start_date)}
                    </S.DataValue>
                  </S.DataRow>
                  <S.DataRow>
                    <S.DataLabel>
                      {t('buildingPermits.detail.fields.inquiryLast')}
                    </S.DataLabel>
                    <S.DataValue>
                      {formatDate(permit.inquiry_last_date)}
                    </S.DataValue>
                  </S.DataRow>
                  <S.DataRow>
                    <S.DataLabel>
                      {t('buildingPermits.detail.fields.approvalDate')}
                    </S.DataLabel>
                    <S.DataValue>
                      {formatDate(permit.inquiry_approval_date)}
                    </S.DataValue>
                  </S.DataRow>
                  {permit.publication_type_name && (
                    <S.DataRow>
                      <S.DataLabel>
                        {t('buildingPermits.detail.fields.publicationType')}
                      </S.DataLabel>
                      <S.DataValue>
                        {permit.publication_type_name}
                        {permit.publication_type_code &&
                          ` (${permit.publication_type_code})`}
                      </S.DataValue>
                    </S.DataRow>
                  )}
                </S.DataColumn>
              </S.DataGrid>
            </S.DataGroup>

            {/* Section 2: Project Description */}
            {permit.description && (
              <S.DataGroup>
                <S.DataSectionTitle>
                  <Icon name="file-text" size="small" />
                  {t('buildingPermits.detail.sections.projectDescription')}
                </S.DataSectionTitle>
                <S.DescriptionBox>{permit.description}</S.DescriptionBox>
              </S.DataGroup>
            )}

            {/* Section 3: Classification & Work Type */}
            <S.DataGroup>
              <S.DataSectionTitle>
                <Icon name="tag" size="small" />
                {t('buildingPermits.detail.sections.classificationWorkType')}
              </S.DataSectionTitle>
              <S.DataGrid>
                <S.DataColumn>
                  {permit.work_type_official && (
                    <S.DataRow>
                      <S.DataLabel>
                        {t('buildingPermits.detail.fields.workType')}
                      </S.DataLabel>
                      <S.DataValue>{permit.work_type_official}</S.DataValue>
                    </S.DataRow>
                  )}
                  {permit.regbl_classification_general && (
                    <S.DataRow>
                      <S.DataLabel>
                        {t('buildingPermits.detail.fields.regblClassification')}
                      </S.DataLabel>
                      <S.DataValue>
                        <S.PropertyBadge
                          $type={permit.regbl_classification_general}
                        >
                          {permit.regbl_classification_general}
                        </S.PropertyBadge>
                      </S.DataValue>
                    </S.DataRow>
                  )}
                  {permit.development_classification_llm && (
                    <S.DataRow>
                      <S.DataLabel>
                        {t(
                          'buildingPermits.detail.fields.developmentPotential'
                        )}
                      </S.DataLabel>
                      <S.DataValue>
                        {permit.development_classification_llm === 'True' ? (
                          <Badge variant="success" size="small">
                            {t(
                              'buildingPermits.detail.developmentPotentialBadge.hasPotential'
                            )}
                          </Badge>
                        ) : (
                          <Badge variant="secondary" size="small">
                            {t(
                              'buildingPermits.detail.developmentPotentialBadge.noPotential'
                            )}
                          </Badge>
                        )}
                      </S.DataValue>
                    </S.DataRow>
                  )}
                </S.DataColumn>

                <S.DataColumn>
                  {regblFlags.length > 0 && (
                    <div>
                      <S.DataLabel style={{ marginBottom: '8px' }}>
                        {t('buildingPermits.detail.fields.regblFeatures')}
                      </S.DataLabel>
                      <S.FlagsList>
                        {regblFlags.map((flag) => (
                          <S.FlagItem key={flag.key}>
                            <Icon name="check" size="small" />
                            {flag.label}
                          </S.FlagItem>
                        ))}
                      </S.FlagsList>
                    </div>
                  )}
                </S.DataColumn>
              </S.DataGrid>
            </S.DataGroup>

            {/* Section 4: Participants */}
            {Object.keys(participantsByRole).length > 0 && (
              <S.DataGroup>
                <S.DataSectionTitle>
                  <Icon name="users" size="small" />
                  {t('buildingPermits.detail.sections.participants', {
                    count: participantsCount,
                  })}
                </S.DataSectionTitle>
                <S.ParticipantsContainer>
                  {/* Owner Group */}
                  {participantsByRole.owner && (
                    <S.ParticipantGroup>
                      <S.ParticipantGroupHeader $role="owner">
                        {t('buildingPermits.detail.participantTypes.owners')}
                        <span>{participantsByRole.owner.length}</span>
                      </S.ParticipantGroupHeader>
                      <S.ParticipantList>
                        {participantsByRole.owner.map((p, idx) => (
                          <S.ParticipantCard key={idx}>
                            <S.ParticipantType>
                              {p.type || 'Unknown'}
                              {p.nationality && ` • ${p.nationality}`}
                            </S.ParticipantType>
                            <S.ParticipantName>
                              {p.full_name ||
                                `${p.first_name || ''} ${p.last_name || ''}`.trim() ||
                                '—'}
                            </S.ParticipantName>
                            {p.roles && p.roles.length > 0 && (
                              <S.ParticipantRole>
                                {p.roles.map((r) => r.role).join(', ')}
                                {p.roles[0].role_organization &&
                                  ` • ${p.roles[0].role_organization}`}
                              </S.ParticipantRole>
                            )}
                            <S.ParticipantDetails>
                              {p.address_street && (
                                <div>{p.address_street}</div>
                              )}
                              {(p.address_postal_code || p.address_city) && (
                                <div>
                                  {p.address_postal_code} {p.address_city}
                                </div>
                              )}
                            </S.ParticipantDetails>
                          </S.ParticipantCard>
                        ))}
                      </S.ParticipantList>
                    </S.ParticipantGroup>
                  )}

                  {/* Architect Group */}
                  {participantsByRole.architect && (
                    <S.ParticipantGroup>
                      <S.ParticipantGroupHeader $role="architect">
                        {t(
                          'buildingPermits.detail.participantTypes.architects'
                        )}
                        <span>{participantsByRole.architect.length}</span>
                      </S.ParticipantGroupHeader>
                      <S.ParticipantList>
                        {participantsByRole.architect.map((p, idx) => (
                          <S.ParticipantCard key={idx}>
                            <S.ParticipantType>
                              {p.type || 'Unknown'}
                              {p.nationality && ` • ${p.nationality}`}
                            </S.ParticipantType>
                            <S.ParticipantName>
                              {p.full_name ||
                                `${p.first_name || ''} ${p.last_name || ''}`.trim() ||
                                '—'}
                            </S.ParticipantName>
                            {p.roles && p.roles.length > 0 && (
                              <S.ParticipantRole>
                                {p.roles.map((r) => r.role).join(', ')}
                                {p.roles[0].role_organization &&
                                  ` • ${p.roles[0].role_organization}`}
                              </S.ParticipantRole>
                            )}
                            <S.ParticipantDetails>
                              {p.address_street && (
                                <div>{p.address_street}</div>
                              )}
                              {(p.address_postal_code || p.address_city) && (
                                <div>
                                  {p.address_postal_code} {p.address_city}
                                </div>
                              )}
                            </S.ParticipantDetails>
                          </S.ParticipantCard>
                        ))}
                      </S.ParticipantList>
                    </S.ParticipantGroup>
                  )}

                  {/* Applicant Group */}
                  {participantsByRole.applicant && (
                    <S.ParticipantGroup>
                      <S.ParticipantGroupHeader $role="applicant">
                        {t(
                          'buildingPermits.detail.participantTypes.applicants'
                        )}
                        <span>{participantsByRole.applicant.length}</span>
                      </S.ParticipantGroupHeader>
                      <S.ParticipantList>
                        {participantsByRole.applicant.map((p, idx) => (
                          <S.ParticipantCard key={idx}>
                            <S.ParticipantType>
                              {p.type || 'Unknown'}
                              {p.nationality && ` • ${p.nationality}`}
                            </S.ParticipantType>
                            <S.ParticipantName>
                              {p.full_name ||
                                `${p.first_name || ''} ${p.last_name || ''}`.trim() ||
                                '—'}
                            </S.ParticipantName>
                            {p.roles && p.roles.length > 0 && (
                              <S.ParticipantRole>
                                {p.roles.map((r) => r.role).join(', ')}
                              </S.ParticipantRole>
                            )}
                            <S.ParticipantDetails>
                              {p.address_street && (
                                <div>{p.address_street}</div>
                              )}
                              {(p.address_postal_code || p.address_city) && (
                                <div>
                                  {p.address_postal_code} {p.address_city}
                                </div>
                              )}
                            </S.ParticipantDetails>
                          </S.ParticipantCard>
                        ))}
                      </S.ParticipantList>
                    </S.ParticipantGroup>
                  )}

                  {/* Other Group */}
                  {participantsByRole.other && (
                    <S.ParticipantGroup>
                      <S.ParticipantGroupHeader>
                        {t('buildingPermits.detail.participantTypes.other')}
                        <span>{participantsByRole.other.length}</span>
                      </S.ParticipantGroupHeader>
                      <S.ParticipantList>
                        {participantsByRole.other.map((p, idx) => (
                          <S.ParticipantCard key={idx}>
                            <S.ParticipantType>
                              {p.type || 'Unknown'}
                            </S.ParticipantType>
                            <S.ParticipantName>
                              {p.full_name ||
                                `${p.first_name || ''} ${p.last_name || ''}`.trim() ||
                                '—'}
                            </S.ParticipantName>
                            {p.roles && p.roles.length > 0 && (
                              <S.ParticipantRole>
                                {p.roles.map((r) => r.role).join(', ')}
                              </S.ParticipantRole>
                            )}
                          </S.ParticipantCard>
                        ))}
                      </S.ParticipantList>
                    </S.ParticipantGroup>
                  )}
                </S.ParticipantsContainer>
              </S.DataGroup>
            )}

            {/* Section 5: Land Parcels */}
            {permit.lands && permit.lands.length > 0 && (
              <S.DataGroup>
                <S.DataSectionTitle>
                  <Icon name="grid" size="small" />
                  {t('buildingPermits.detail.sections.landParcels', {
                    count: permit.lands.length,
                  })}
                </S.DataSectionTitle>
                {permit.lands.map((land, idx) => (
                  <S.PropertyCard key={idx}>
                    <S.PropertyHeader>
                      <S.PropertyHeaderLeft>
                        <S.MapMarkerBadge>{idx + 1}</S.MapMarkerBadge>
                        <span>
                          {t('buildingPermits.detail.fields.parcel')}{' '}
                          {land.code_number ||
                            land.land_register_number ||
                            land.id ||
                            `#${idx + 1}`}
                        </span>
                      </S.PropertyHeaderLeft>
                      <S.DataValue>
                        {land.area
                          ? formatLandArea
                            ? formatLandArea(land.area)
                            : `${land.area.toLocaleString('de-CH')} m²`
                          : '—'}
                      </S.DataValue>
                    </S.PropertyHeader>
                    <S.PropertyInfo>
                      <S.PropertyInfoRow>
                        <span>
                          {t('buildingPermits.detail.fields.parcelNumber')}
                        </span>
                        <span>
                          {land.code_number || land.land_register_number || '—'}
                        </span>
                      </S.PropertyInfoRow>
                      <S.PropertyInfoRow>
                        <span>{t('buildingPermits.detail.fields.area')}</span>
                        <span>
                          {land.area
                            ? `${land.area.toLocaleString('de-CH')} m²`
                            : '—'}
                        </span>
                      </S.PropertyInfoRow>
                    </S.PropertyInfo>
                  </S.PropertyCard>
                ))}
              </S.DataGroup>
            )}

            {/* Section 6: Construction Features */}
            {permit.features && permit.features.length > 0 && (
              <S.DataGroup>
                <S.DataSectionTitle>
                  <Icon name="wrench" size="small" />
                  {t('buildingPermits.detail.sections.constructionFeatures', {
                    count: permit.features.length,
                  })}
                </S.DataSectionTitle>
                {permit.features.map((feature, idx) => (
                  <S.PropertyCard key={idx}>
                    <S.PropertyHeader>
                      <S.PropertyHeaderLeft>
                        <S.FeatureIcon>
                          {feature.action?.toLowerCase() === 'transformation'
                            ? '🔧'
                            : feature.action?.toLowerCase() === 'construction'
                              ? '🏗️'
                              : feature.action?.toLowerCase() === 'demolition'
                                ? '🔨'
                                : '⚙️'}
                        </S.FeatureIcon>
                        <S.FeatureContent>
                          <S.FeatureTitle>
                            {feature.action ||
                              t('buildingPermits.detail.fields.unknownAction')}
                          </S.FeatureTitle>
                        </S.FeatureContent>
                      </S.PropertyHeaderLeft>
                    </S.PropertyHeader>
                    <S.PropertyInfo>
                      {feature.category && (
                        <S.PropertyInfoRow>
                          <span>
                            {t('buildingPermits.detail.fields.category')}
                          </span>
                          <span>{feature.category}</span>
                        </S.PropertyInfoRow>
                      )}
                      {feature.subcategory && (
                        <S.PropertyInfoRow>
                          <span>
                            {t('buildingPermits.detail.fields.subcategory')}
                          </span>
                          <span>{feature.subcategory}</span>
                        </S.PropertyInfoRow>
                      )}
                      {feature.usage_category && (
                        <S.PropertyInfoRow>
                          <span>
                            {t('buildingPermits.detail.fields.usage')}
                          </span>
                          <span>{feature.usage_category}</span>
                        </S.PropertyInfoRow>
                      )}
                      {feature.count !== undefined && (
                        <S.PropertyInfoRow>
                          <span>
                            {t('buildingPermits.detail.fields.count')}
                          </span>
                          <span>{feature.count}</span>
                        </S.PropertyInfoRow>
                      )}
                    </S.PropertyInfo>
                  </S.PropertyCard>
                ))}
              </S.DataGroup>
            )}

            {/* Section 7: Land Use Types */}
            {permit.land_use_principal_types &&
              permit.land_use_principal_types.length > 0 && (
                <S.DataGroup>
                  <S.DataSectionTitle>
                    <Icon name="layers" size="small" />
                    {t('buildingPermits.detail.sections.landUseTypes')}
                  </S.DataSectionTitle>
                  <S.LandUseList>
                    {permit.land_use_principal_types.map((landUse, idx) => (
                      <S.LandUseItem key={idx}>
                        {landUse.land_use_type || '—'}
                        {landUse.percentage !== undefined &&
                          ` (${landUse.percentage}%)`}
                      </S.LandUseItem>
                    ))}
                  </S.LandUseList>
                </S.DataGroup>
              )}

            {/* External Link */}
            {permit.url_web && (
              <S.DataGroup>
                <S.ExternalLinkButton
                  href={permit.url_web}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Icon name="arrow-u-up-right" size="small" />
                  {t('buildingPermits.detail.viewDocument')}
                </S.ExternalLinkButton>
              </S.DataGroup>
            )}
          </S.DetailContent>
          </>
        )}
      </DetailPanelContent>
    </ResizableDetailPanel>
  )
}
